import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DialogHeader } from '@/components/dialog/dialog-header';
import { useToastContext } from '@/layout/context/toast-context';
import { deleteGreetingFile, downloadGreetingBlob, fetchGreetingMetadata, uploadGreetingFile, } from '@/services/files/greetingFileApi';
import { reloadUserCallGreeting } from '@/shared/providers/sip-call-provider/callGreeting';
import { formatGreetingCreatedAt } from '@/shared/model/greeting-file.model';
import { resolveGreetingUploadedBy } from '@/shared/utils/resolve-greeting-uploaded-by';
import { useAppSelector } from '@/store';
const ACCEPT = 'audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/ogg,.mp3,.wav,.m4a,.ogg';
const MAX_BYTES = 15 * 1024 * 1024;
function formatBytes(n) {
    if (n < 1024)
        return `${n} B`;
    if (n < 1024 * 1024)
        return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
export default function GreetingUploadDialog({ visible, onHide }) {
    const inputId = useId();
    const fileInputRef = useRef(null);
    const audioRef = useRef(null);
    const playbackUrlRef = useRef(null);
    const { showToast } = useToastContext();
    const session = useAppSelector((state) => state.auth.session);
    const uploadedBy = resolveGreetingUploadedBy(session);
    const [currentGreeting, setCurrentGreeting] = useState(null);
    const [metadataLoading, setMetadataLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [playbackLoading, setPlaybackLoading] = useState(false);
    const hasExistingGreeting = currentGreeting != null;
    const busy = uploading || deleting || metadataLoading || playbackLoading;
    const stopPlayback = useCallback(() => {
        const audio = audioRef.current;
        if (audio) {
            audio.pause();
            audio.removeAttribute('src');
            audio.load();
        }
        if (playbackUrlRef.current) {
            URL.revokeObjectURL(playbackUrlRef.current);
            playbackUrlRef.current = null;
        }
        setPlaying(false);
    }, []);
    const resetSelection = useCallback(() => {
        setSelectedFile(null);
        setIsDragging(false);
        if (fileInputRef.current)
            fileInputRef.current.value = '';
    }, []);
    const reset = useCallback(() => {
        resetSelection();
        setError(null);
        setUploading(false);
        setDeleting(false);
        setCurrentGreeting(null);
        setMetadataLoading(false);
        setPlaybackLoading(false);
        stopPlayback();
    }, [resetSelection, stopPlayback]);
    const loadMetadata = useCallback(async () => {
        if (!uploadedBy) {
            setCurrentGreeting(null);
            return;
        }
        setMetadataLoading(true);
        setError(null);
        try {
            const metadata = await fetchGreetingMetadata(uploadedBy);
            setCurrentGreeting(metadata);
            if (metadata) {
                resetSelection();
            }
        }
        catch (e) {
            const detail = e instanceof Error ? e.message : 'Could not load your greeting.';
            setError(detail);
        }
        finally {
            setMetadataLoading(false);
        }
    }, [resetSelection, uploadedBy]);
    useEffect(() => {
        if (!visible) {
            reset();
            return;
        }
        void loadMetadata();
    }, [visible, loadMetadata, reset]);
    const handleHide = useCallback(() => {
        reset();
        onHide();
    }, [onHide, reset]);
    const validateAndSetFile = useCallback((file) => {
        if (!file || hasExistingGreeting)
            return;
        setError(null);
        if (!file.type.startsWith('audio/') && !/\.(mp3|wav|m4a|ogg)$/i.test(file.name)) {
            setError('Please choose an audio file (MP3, WAV, M4A, or OGG).');
            setSelectedFile(null);
            return;
        }
        if (file.size > MAX_BYTES) {
            setError(`File is too large. Maximum size is ${formatBytes(MAX_BYTES)}.`);
            setSelectedFile(null);
            return;
        }
        setSelectedFile(file);
    }, [hasExistingGreeting]);
    const onInputChange = (e) => {
        validateAndSetFile(e.target.files?.[0]);
    };
    const onDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        validateAndSetFile(e.dataTransfer.files?.[0]);
    };
    const onDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };
    const onUpload = async () => {
        if (!selectedFile || hasExistingGreeting)
            return;
        if (!uploadedBy) {
            setError('Could not resolve your account email for greeting upload.');
            return;
        }
        setUploading(true);
        setError(null);
        try {
            const metadata = await uploadGreetingFile(selectedFile);
            await reloadUserCallGreeting(uploadedBy);
            setCurrentGreeting(metadata);
            resetSelection();
            showToast({
                severity: 'success',
                summary: 'Greeting saved',
                detail: `"${metadata.originalFileName}" is now your call greeting.`,
                life: 4500,
            });
        }
        catch (e) {
            const detail = e instanceof Error ? e.message : 'Could not upload the greeting file.';
            setError(detail);
            showToast({
                severity: 'error',
                summary: 'Upload failed',
                detail,
                life: 5000,
            });
        }
        finally {
            setUploading(false);
        }
    };
    const onPlayCurrent = async () => {
        if (!uploadedBy || !currentGreeting)
            return;
        stopPlayback();
        setPlaybackLoading(true);
        setError(null);
        try {
            const blob = await downloadGreetingBlob(uploadedBy);
            if (!blob) {
                setError('Could not load the greeting audio for playback.');
                return;
            }
            const url = URL.createObjectURL(blob);
            playbackUrlRef.current = url;
            const audio = audioRef.current;
            if (!audio)
                return;
            audio.src = url;
            await audio.play();
            setPlaying(true);
        }
        catch {
            setError('Could not play the greeting audio.');
            stopPlayback();
        }
        finally {
            setPlaybackLoading(false);
        }
    };
    const onDeleteCurrent = async () => {
        if (!currentGreeting?.id || !uploadedBy)
            return;
        if (!window.confirm('Remove your current call greeting? You can upload a new one after it is deleted.')) {
            return;
        }
        setDeleting(true);
        setError(null);
        stopPlayback();
        try {
            await deleteGreetingFile(currentGreeting.id);
            await reloadUserCallGreeting(uploadedBy);
            setCurrentGreeting(null);
            showToast({
                severity: 'success',
                summary: 'Greeting removed',
                detail: 'You can upload a new greeting audio file.',
                life: 4000,
            });
        }
        catch (e) {
            const detail = e instanceof Error ? e.message : 'Could not delete the greeting file.';
            setError(detail);
            showToast({
                severity: 'error',
                summary: 'Delete failed',
                detail,
                life: 5000,
            });
        }
        finally {
            setDeleting(false);
        }
    };
    const footer = (_jsxs("div", { className: "greeting-upload-dialog__footer", children: [_jsx(Button, { type: "button", label: "Close", icon: "pi pi-times", className: "p-button-text", disabled: busy && !hasExistingGreeting, onClick: handleHide }), !hasExistingGreeting ? (_jsx(Button, { type: "button", label: "Use as greeting", icon: "pi pi-check", disabled: !selectedFile || uploading || !uploadedBy, loading: uploading, onClick: () => void onUpload() })) : null] }));
    return (_jsxs(Dialog, { className: "greeting-upload-dialog", contentClassName: "greeting-upload-dialog__body", header: _jsx(DialogHeader, { icon: "pi pi-microphone", label: "Greeting audio" }), visible: visible, modal: true, draggable: false, resizable: false, dismissableMask: !busy, closable: !busy, footer: footer, style: { width: 'min(30rem, 94vw)' }, onHide: handleHide, children: [_jsx("p", { className: "greeting-upload-dialog__lead", children: hasExistingGreeting
                    ? 'Your current greeting plays to callers when you answer. Delete it to upload a different clip.'
                    : 'Upload a short audio clip callers hear before you answer — drag and drop or browse your device.' }), !uploadedBy ? (_jsxs("p", { className: "greeting-upload-dialog__error", role: "alert", children: [_jsx("i", { className: "pi pi-exclamation-circle", "aria-hidden": true }), " Account email is required to save your greeting."] })) : null, metadataLoading ? (_jsxs("div", { className: "greeting-upload-dialog__loading flex align-items-center gap-2 text-sm text-color-secondary mb-3", children: [_jsx(ProgressSpinner, { style: { width: '1.25rem', height: '1.25rem' }, strokeWidth: "4" }), _jsx("span", { children: "Loading current greeting\u2026" })] })) : null, hasExistingGreeting && currentGreeting ? (_jsxs("section", { className: "greeting-upload-dialog__current", "aria-label": "Current greeting", children: [_jsxs("div", { className: "greeting-upload-dialog__current-head", children: [_jsx("span", { className: "greeting-upload-dialog__current-label", children: "Current greeting" }), _jsx("span", { className: "greeting-upload-dialog__current-date", children: formatGreetingCreatedAt(currentGreeting.createdAt) })] }), _jsxs("div", { className: "greeting-upload-dialog__file greeting-upload-dialog__file--current", children: [_jsx("i", { className: "pi pi-volume-up greeting-upload-dialog__file-icon", "aria-hidden": true }), _jsxs("div", { className: "greeting-upload-dialog__file-meta", children: [_jsx("span", { className: "greeting-upload-dialog__file-name", children: currentGreeting.originalFileName }), _jsxs("span", { className: "greeting-upload-dialog__file-size", children: [currentGreeting.sizeBytes != null ? formatBytes(currentGreeting.sizeBytes) : '—', currentGreeting.contentType ? ` · ${currentGreeting.contentType}` : ''] })] })] }), _jsxs("div", { className: "greeting-upload-dialog__current-actions", children: [_jsx(Button, { type: "button", label: playing ? 'Playing' : 'Play', icon: "pi pi-play", size: "small", outlined: true, disabled: playing || deleting, loading: playbackLoading, onClick: () => void onPlayCurrent() }), _jsx(Button, { type: "button", label: "Delete", icon: "pi pi-trash", size: "small", severity: "danger", outlined: true, disabled: playbackLoading || deleting, loading: deleting, onClick: () => void onDeleteCurrent() })] }), _jsx("audio", { ref: audioRef, className: "greeting-upload-dialog__audio", preload: "none", onEnded: () => stopPlayback(), onPause: () => setPlaying(false) })] })) : null, !hasExistingGreeting && uploadedBy ? (_jsxs(_Fragment, { children: [_jsx("input", { ref: fileInputRef, id: inputId, type: "file", className: "greeting-upload-dialog__input", accept: ACCEPT, disabled: uploading || metadataLoading, onChange: onInputChange }), _jsxs("label", { htmlFor: inputId, className: `greeting-upload-dialog__drop${isDragging ? ' greeting-upload-dialog__drop--active' : ''}${uploading || metadataLoading ? ' greeting-upload-dialog__drop--disabled' : ''}`, onDragEnter: () => !uploading && setIsDragging(true), onDragLeave: () => setIsDragging(false), onDragOver: onDragOver, onDrop: onDrop, children: [_jsx("span", { className: "greeting-upload-dialog__drop-icon", "aria-hidden": true, children: _jsx("i", { className: "pi pi-cloud-upload" }) }), _jsx("span", { className: "greeting-upload-dialog__drop-title", children: selectedFile ? 'Replace file' : 'Drop audio here' }), _jsxs("span", { className: "greeting-upload-dialog__drop-hint", children: ["MP3, WAV, M4A, OGG \u00B7 up to ", formatBytes(MAX_BYTES)] }), _jsx("span", { className: "greeting-upload-dialog__browse", children: "Browse files" })] }), selectedFile ? (_jsxs("div", { className: "greeting-upload-dialog__file", role: "status", children: [_jsx("i", { className: "pi pi-file greeting-upload-dialog__file-icon", "aria-hidden": true }), _jsxs("div", { className: "greeting-upload-dialog__file-meta", children: [_jsx("span", { className: "greeting-upload-dialog__file-name", children: selectedFile.name }), _jsx("span", { className: "greeting-upload-dialog__file-size", children: formatBytes(selectedFile.size) })] }), _jsx(Button, { type: "button", icon: "pi pi-times", className: "p-button-text p-button-secondary greeting-upload-dialog__remove", "aria-label": "Clear selected file", disabled: uploading, onClick: (e) => {
                                    e.preventDefault();
                                    resetSelection();
                                } })] })) : null] })) : null, error ? (_jsxs("p", { className: "greeting-upload-dialog__error", role: "alert", children: [_jsx("i", { className: "pi pi-exclamation-circle", "aria-hidden": true }), " ", error] })) : null] }));
}
